<template>
  <section class="m-b-25 m-t-20">
    <ValidationProvider
      ref="validationProvider"
      :rules="rules"
      v-slot="validationProps"
      :value="computedValue"
    >
      <div
        :class="{
          columns: isHorizontal,
          'is-vcentered': isHorizontal,
          'is-multiline': isHorizontal && isMultiline,
          'm-b-40': !isHorizontal
        }"
        :data-e2e="FormCypressAttribute.views.BOX_CHECKBOX_GROUP_WRAPPER"
      >
        <template v-for="option in options" :key="option[trackBy]">
          <div class="box box-button box-radio is-narrow m-b-10" :class="getBoxClasses(option)">
            <div
              @click.prevent="onClick(option)"
              :data-e2e="FormCypressAttribute.views.BOX_CHECKBOX_GROUP_LIST"
            >
              <!-- vee-validate needs something with a v-model-->
              <label>
                <input v-model="computedValue" style="display: none" />
              </label>
              <template v-if="!hasCheckbox">
                <div class="is-flex is-align-items-center p-1">
                  <slot name="body" :option="option">
                    <i
                      v-if="isSelected(option)"
                      :class="`cls ${customSelectedIcon} m-r-5 has-text-primary is-size-5 m-r-10`"
                    />
                    <p class="is-size-4 has-text-weight-normal m-b-2">{{ getValue(option) }}</p>
                  </slot>
                </div>
              </template>
              <FieldView v-else>
                <CheckboxButtonView
                  v-if="isButton"
                  :value="computedValue"
                  type="is-light"
                  :class="{ 'is-full-width': !isHorizontal, border: 'none' }"
                  :native-value="option[trackBy]"
                  @click.native.prevent
                >
                  <slot name="body" :option="option">
                    <p>{{ getValue(option) }}</p>
                  </slot>
                </CheckboxButtonView>
                <CheckboxView
                  v-else
                  :value="computedValue"
                  :class="{ 'is-full-width': !isHorizontal }"
                  :native-value="option[trackBy]"
                  @click.native.prevent
                >
                  <slot name="body" :option="option">
                    <p>{{ getValue(option) }}</p>
                  </slot>
                </CheckboxView>
              </FieldView>
            </div>
            <slot name="main" :option="option"></slot>
          </div>

          <slot name="footer-option" :option="option"></slot>
        </template>
      </div>
      <slot name="error" v-bind="validationProps">
        <span class="has-text-danger is-size-7 m-t-3 is-absolute">
          {{ getErrorFormattedText(validationProps) }}
        </span>
      </slot>
    </ValidationProvider>
  </section>
</template>

<script>
import ValidationProvider from '/@/modules/core/form/components/ValidationProvider.vue';
import { FormComputedValueMixin } from '/@/modules/core/form/mixins/FormComputedValueMixin';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import { FormBoxMixin } from '/@/modules/core/form/mixins/FormBoxMixin';
import { FormCypressAttribute } from '/@/modules/core/form/constants/FormCypressAttribute';
import FieldView from '@/modules/core/ui/components/wrappers/FieldView.vue';
import CheckboxView from '@/modules/core/ui/components/wrappers/CheckboxView.vue';
import CheckboxButtonView from '@/modules/core/ui/components/wrappers/CheckboxButtonView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormBoxCheckboxGroup',
  emits: ['on-checkbox-checked', 'on-checkbox-unchecked', 'input', 'click'],
  slotsDefinition: {
    body: { option: Object },
    main: { option: Object },
    footerOption: { option: Object },
    error: { validationProps: Object }
  },
  mixins: [FormComputedValueMixin, FormValidationMixin, FormBoxMixin],
  components: { CheckboxView, CheckboxButtonView, FieldView, ValidationProvider },
  props: {
    disabledOptions: {
      type: Array,
      default: () => []
    },
    isButton: {
      type: Boolean,
      default: false
    },
    hasCheckbox: {
      type: Boolean,
      default: true
    },
    hasBorder: {
      type: Boolean,
      default: true
    },
    customSelectedIcon: {
      type: String,
      default: null
    },
    customUnselectedBackgroundColor: {
      type: String,
      default: ''
    },
    customSelectedBackgroundColor: {
      type: String,
      default: ''
    }
  },
  static: {
    FormCypressAttribute
  },
  methods: {
    getBoxClasses(option) {
      const selected = this.isSelected(option) ? 'selected' : '';
      const isHorizontal = this.isHorizontal ? 'is-horizontal' : '';
      const disabled = this.isDisabled(option) ? 'is-disabled has-text-secondary-grey' : '';
      const removePadding = this.isButton || this.hasCheckbox ? 'p-0' : '';
      const hasBorder = this.hasBorder ? '' : 'is-shadowless is-borderless';
      const customSpacing = this.hasCheckbox ? '' : 'm-r-10';
      const backgroundClass = this.isDisabled(option)
        ? ''
        : this.isSelected(option)
        ? this.customSelectedBackgroundColor
        : this.customUnselectedBackgroundColor;
      return `${isHorizontal} ${selected} ${this.size} ${disabled} ${removePadding} ${backgroundClass} ${hasBorder} ${customSpacing}`;
    },
    isDisabled(option) {
      return this.disabledOptions.some(
        (disabledOption) => disabledOption[this.trackBy] === option[this.trackBy]
      );
    },
    onClick(option) {
      if (!this.isDisabled(option)) {
        const value = option[this.trackBy];
        const index = this.computedValue.indexOf(value);
        if (index === -1) {
          this.$emit('on-checkbox-checked', option);
          this.computedValue.push(value);
        } else {
          this.$emit('on-checkbox-unchecked', option);
          this.computedValue.splice(index, 1);
        }
        this.$emit('input', this.computedValue);
        this.$emit('click', value);
        this.$nextTick(() => {
          this.$refs.validatorObserver?.validate();
        });
      }
    }
  }
};
</script>

<style scoped>
.box {
  &.box-button {
    &.auto {
      width: auto;
      min-width: var(--ce-60px);
    }

    &.small {
      width: unset;
      min-width: var(--ce-80px);
    }

    &.medium {
      width: auto;
      min-width: var(--ce-200px);
    }

    &.large {
      width: 100%;
    }
  }
}

:deep(.box.box-button .label) {
  margin-bottom: 0 !important;
}
</style>
